// =============================================================================
// useApiCosts — agregacja llm_call_events dla strony /koszty-api
// =============================================================================
// Wszystkie query po stronie klienta (RLS: is_allowed_user()). Bez Realtime tutaj
// — panel agregatorowy, refresh manualny lub na focus.
//
// Zwraca:
//   - totals (dzis / wczoraj / tydzien / miesiac) + delta vs poprzedni okres
//   - daily per-model breakdown (30 dni) dla stacked line chart
//   - top steps najdrozsze (14 dni)
//   - top maps najdrozsze (14 dni)

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PeriodTotals {
  today: number;
  yesterday: number;
  week: number;
  prevWeek: number;
  month: number;
  prevMonth: number;
}

export interface DailyModelCost {
  date: string;     // YYYY-MM-DD
  model: string;
  cost: number;
}

export interface TopStep {
  step: string | null;
  cost: number;
  calls: number;
  avg_cost: number;
}

export interface TopMap {
  map_id: string;
  map_name: string | null;
  cost: number;
  calls: number;
}

export interface ApiCostsData {
  totals: PeriodTotals;
  daily: DailyModelCost[];
  topSteps: TopStep[];
  topMaps: TopMap[];
  loading: boolean;
  refresh: () => void;
  lastFetchedAt: Date | null;
}

function startOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}

function sumCost(rows: Array<{ cost_usd: number | string }>): number {
  return rows.reduce((acc, r) => acc + Number(r.cost_usd ?? 0), 0);
}

export function useApiCosts(): ApiCostsData {
  const [totals, setTotals] = useState<PeriodTotals>({
    today: 0, yesterday: 0, week: 0, prevWeek: 0, month: 0, prevMonth: 0,
  });
  const [daily, setDaily] = useState<DailyModelCost[]>([]);
  const [topSteps, setTopSteps] = useState<TopStep[]>([]);
  const [topMaps, setTopMaps] = useState<TopMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);

    const now = new Date();
    const todayStart = startOfDay(now);
    const yesterdayStart = new Date(todayStart); yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const weekStart = new Date(todayStart); weekStart.setDate(weekStart.getDate() - 6);
    const prevWeekStart = new Date(weekStart); prevWeekStart.setDate(prevWeekStart.getDate() - 7);
    const monthStart = new Date(todayStart); monthStart.setDate(monthStart.getDate() - 29);
    const prevMonthStart = new Date(monthStart); prevMonthStart.setDate(prevMonthStart.getDate() - 30);
    const chart30Start = new Date(todayStart); chart30Start.setDate(chart30Start.getDate() - 29);
    const topWindowStart = new Date(todayStart); topWindowStart.setDate(topWindowStart.getDate() - 13);

    // --- Totals (one query, client-side bucket)
    const totalsFetch = await (supabase as any)
      .from("llm_call_events")
      .select("cost_usd, created_at")
      .gte("created_at", prevMonthStart.toISOString())
      .limit(50000);

    const totalsRows = (totalsFetch.data || []) as Array<{ cost_usd: number | string; created_at: string }>;

    const bucket = (start: Date, end: Date) =>
      totalsRows.filter((r) => {
        const t = new Date(r.created_at).getTime();
        return t >= start.getTime() && t < end.getTime();
      });

    const tomorrow = new Date(todayStart); tomorrow.setDate(tomorrow.getDate() + 1);
    const weekEnd = tomorrow;
    const monthEnd = tomorrow;

    const newTotals: PeriodTotals = {
      today: sumCost(bucket(todayStart, tomorrow)),
      yesterday: sumCost(bucket(yesterdayStart, todayStart)),
      week: sumCost(bucket(weekStart, weekEnd)),
      prevWeek: sumCost(bucket(prevWeekStart, weekStart)),
      month: sumCost(bucket(monthStart, monthEnd)),
      prevMonth: sumCost(bucket(prevMonthStart, monthStart)),
    };

    // --- Daily per model (30 days) — client-side group by day + model
    const dailyFetch = await (supabase as any)
      .from("llm_call_events")
      .select("cost_usd, model, created_at")
      .gte("created_at", chart30Start.toISOString())
      .limit(50000);

    const dailyRows = (dailyFetch.data || []) as Array<{ cost_usd: number | string; model: string; created_at: string }>;
    const dailyMap = new Map<string, number>();  // key: `${date}|${model}`
    for (const r of dailyRows) {
      const date = r.created_at.slice(0, 10);
      const key = `${date}|${r.model}`;
      dailyMap.set(key, (dailyMap.get(key) || 0) + Number(r.cost_usd || 0));
    }
    const dailyArr: DailyModelCost[] = [];
    for (const [key, cost] of dailyMap.entries()) {
      const [date, model] = key.split("|");
      dailyArr.push({ date, model, cost });
    }
    dailyArr.sort((a, b) => a.date.localeCompare(b.date));

    // --- Top steps (14 days) — client-side group
    const topFetch = await (supabase as any)
      .from("llm_call_events")
      .select("cost_usd, step, map_id")
      .gte("created_at", topWindowStart.toISOString())
      .limit(50000);

    const topRows = (topFetch.data || []) as Array<{ cost_usd: number | string; step: string | null; map_id: string | null }>;

    const stepMap = new Map<string, { cost: number; calls: number }>();
    const mapMap = new Map<string, { cost: number; calls: number }>();
    for (const r of topRows) {
      const stepKey = r.step ?? "(unknown)";
      const s = stepMap.get(stepKey) ?? { cost: 0, calls: 0 };
      s.cost += Number(r.cost_usd || 0);
      s.calls += 1;
      stepMap.set(stepKey, s);

      if (r.map_id) {
        const m = mapMap.get(r.map_id) ?? { cost: 0, calls: 0 };
        m.cost += Number(r.cost_usd || 0);
        m.calls += 1;
        mapMap.set(r.map_id, m);
      }
    }

    const topStepsArr: TopStep[] = [...stepMap.entries()]
      .map(([step, v]) => ({ step: step === "(unknown)" ? null : step, cost: v.cost, calls: v.calls, avg_cost: v.cost / v.calls }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 10);

    // Map names
    const mapIds = [...mapMap.keys()];
    let mapNameById = new Map<string, string | null>();
    if (mapIds.length > 0) {
      const { data: maps } = await (supabase as any)
        .from("thematic_maps")
        .select("id, name")
        .in("id", mapIds)
        .is("map_kind", null);  // standalone mode: ukryj mapy z map_kind='standalone'
      for (const m of (maps || []) as Array<{ id: string; name: string | null }>) {
        mapNameById.set(m.id, m.name);
      }
    }
    const topMapsArr: TopMap[] = [...mapMap.entries()]
      .map(([id, v]) => ({ map_id: id, map_name: mapNameById.get(id) ?? null, cost: v.cost, calls: v.calls }))
      .sort((a, b) => b.cost - a.cost)
      .slice(0, 10);

    setTotals(newTotals);
    setDaily(dailyArr);
    setTopSteps(topStepsArr);
    setTopMaps(topMapsArr);
    setLastFetchedAt(new Date());
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return { totals, daily, topSteps, topMaps, loading, refresh: fetchAll, lastFetchedAt };
}
